import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useConfirmation } from "@/components/ConfirmationProvider";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, CheckSquare, Edit2, Trash2, Calendar, User, Square, CheckCircle2, Clock } from "lucide-react";
import { format, parseISO, isToday, isPast, isFuture } from "date-fns";

const CATEGORIES = ["therapy", "exercise", "practice", "medication", "appointment", "other"];
const PRIORITIES = ["low", "medium", "high"];

export default function ActivitiesPage() {
  const confirm = useConfirmation();
  const [activities, setActivities] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({
    child_id: "",
    title: "",
    description: "",
    category: "therapy",
    due_date: "",
    priority: "medium"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [activitiesRes, childrenRes] = await Promise.all([
        apiClient.get("/activities"),
        apiClient.get("/children")
      ]);
      setActivities(activitiesRes.data);
      setChildren(childrenRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingActivity) {
        await apiClient.put(`/activities/${editingActivity.id}`, form);
        toast.success("Activity updated!");
      } else {
        await apiClient.post("/activities", form);
        toast.success("Activity added!");
      }
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ title: "Delete activity?", description: "This activity will be permanently removed." }))) return;
    try {
      await apiClient.delete(`/activities/${id}`);
      toast.success("Activity deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const toggleComplete = async (activity) => {
    try {
      await apiClient.put(`/activities/${activity.id}`, { completed: !activity.completed });
      fetchData();
      toast.success(activity.completed ? "Marked incomplete" : "Great job! Activity completed!");
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const openEdit = (activity) => {
    setEditingActivity(activity);
    setForm({
      child_id: activity.child_id,
      title: activity.title,
      description: activity.description,
      category: activity.category,
      due_date: activity.due_date,
      priority: activity.priority
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setForm({
      child_id: "",
      title: "",
      description: "",
      category: "therapy",
      due_date: "",
      priority: "medium"
    });
    setEditingActivity(null);
    setDialogOpen(false);
  };

  const getChildName = (id) => children.find(c => c.id === id)?.name || "Unknown";

  const getPriorityColor = (priority) => {
    const colors = {
      high: "bg-coral-100 text-coral-500",
      medium: "bg-golden-100 text-golden-500",
      low: "bg-teal-100 text-teal-700"
    };
    return colors[priority] || "bg-muted text-muted-foreground";
  };

  const getCategoryIcon = (cat) => {
    return CheckSquare;
  };

  const filteredActivities = activities.filter(a => {
    if (filter === "all") return true;
    if (filter === "pending") return !a.completed;
    if (filter === "completed") return a.completed;
    if (filter === "today") {
      try {
        return isToday(parseISO(a.due_date));
      } catch {
        return false;
      }
    }
    if (filter === "overdue") {
      try {
        return !a.completed && isPast(parseISO(a.due_date)) && !isToday(parseISO(a.due_date));
      } catch {
        return false;
      }
    }
    return true;
  });

  const sortedActivities = [...filteredActivities].sort((a, b) => {
    // Completed at bottom
    if (a.completed !== b.completed) return a.completed ? 1 : -1;
    // Priority order
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-10 bg-muted rounded-lg w-48" />
        <div className="space-y-3">
          {[1,2,3,4,5].map(i => <div key={i} className="h-20 bg-muted rounded-3xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="activities-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3">
            <CheckSquare className="h-8 w-8" />
            Activity Planner
          </h1>
          <p className="text-muted-foreground mt-1">Plan and track therapy activities & strategies</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-activity-btn">
              <Plus className="mr-2 h-4 w-4" />
              Add Activity
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingActivity ? "Edit Activity" : "Add Activity"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Child</Label>
                <Select value={form.child_id} onValueChange={(v) => setForm({ ...form, child_id: v })} required>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Select child" />
                  </SelectTrigger>
                  <SelectContent>
                    {children.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Activity Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Practice speech exercises"
                  required
                  className="rounded-xl"
                  data-testid="activity-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Details about the activity..."
                  className="rounded-xl resize-none"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => (
                        <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map(p => (
                        <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={form.due_date}
                    onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={resetForm} className="flex-1 rounded-full">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 rounded-full" data-testid="save-activity-btn">
                  {editingActivity ? "Update" : "Add"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {[
          { key: "all", label: "All" },
          { key: "pending", label: "Pending" },
          { key: "today", label: "Today" },
          { key: "overdue", label: "Overdue" },
          { key: "completed", label: "Completed" }
        ].map(f => (
          <Button
            key={f.key}
            variant={filter === f.key ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(f.key)}
            className="rounded-full"
          >
            {f.label}
          </Button>
        ))}
      </div>

      {/* Activities List */}
      {activities.length === 0 ? (
        <Card className="border-0 shadow-lg rounded-3xl">
          <CardContent className="py-16 text-center">
            <CheckSquare className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">No Activities Yet</h3>
            <p className="text-muted-foreground mb-6">Plan therapy activities and daily strategies</p>
            <Button onClick={() => setDialogOpen(true)} className="rounded-full">
              <Plus className="mr-2 h-4 w-4" />
              Add Activity
            </Button>
          </CardContent>
        </Card>
      ) : sortedActivities.length === 0 ? (
        <Card className="border-0 shadow-lg rounded-3xl">
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No activities match this filter</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {sortedActivities.map((activity, i) => (
            <Card 
              key={activity.id} 
              className={`border-0 shadow-lg rounded-3xl transition-all duration-300 animate-fadeIn ${
                activity.completed ? "opacity-60" : ""
              }`}
              data-testid={`activity-item-${activity.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => toggleComplete(activity)}
                    className={`h-10 w-10 rounded-xl flex items-center justify-center transition-all flex-shrink-0 ${
                      activity.completed 
                        ? "bg-success/10" 
                        : "bg-muted hover:bg-primary/10"
                    }`}
                    data-testid={`toggle-activity-${activity.id}`}
                  >
                    {activity.completed ? (
                      <CheckCircle2 className="h-6 w-6 text-success" />
                    ) : (
                      <Square className="h-6 w-6 text-muted-foreground" />
                    )}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className={`font-heading font-bold ${activity.completed ? "line-through text-muted-foreground" : ""}`}>
                        {activity.title}
                      </h3>
                      <Badge className={`rounded-full text-xs ${getPriorityColor(activity.priority)}`}>
                        {activity.priority}
                      </Badge>
                      <Badge variant="secondary" className="rounded-full text-xs capitalize">
                        {activity.category}
                      </Badge>
                    </div>
                    {activity.description && (
                      <p className="text-sm text-muted-foreground line-clamp-1 mb-1">{activity.description}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {getChildName(activity.child_id)}
                      </span>
                      {activity.due_date && (
                        <span className={`flex items-center gap-1 ${
                          !activity.completed && isPast(parseISO(activity.due_date)) && !isToday(parseISO(activity.due_date))
                            ? "text-destructive"
                            : ""
                        }`}>
                          <Clock className="h-3 w-3" />
                          {isToday(parseISO(activity.due_date)) 
                            ? "Today" 
                            : format(parseISO(activity.due_date), "MMM d")}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(activity)} className="rounded-full h-9 w-9">
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleDelete(activity.id)}
                      className="rounded-full h-9 w-9 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
